import { FormatDateDataType } from "../../util/genericDateAndTimeFuncs";
import { lddGlobalSessionInfo } from "../navigationTypes";
import fieldDef from "./FieldDef";
import { v4 as uuidv4 } from "uuid";

export type EmailBody = {
  to: string;
  subject: string;
  msg: any;
  isHTML?: boolean;
};

export type LenderBranches = {
  branch: string;
  lenderContactID: number;
};

namespace AnalyzerFuncs {
  export async function upload(
    doc: File,
    SESSION_INFO: lddGlobalSessionInfo,
    AppID: string,
    setDocID: (docID: string) => void,
    successCallback: (...arg) => void,
    failedCallback: (...arg) => void,
  ): Promise<any> {
    const { appProvinceShort, firm, sid } = SESSION_INFO;
    const RWAPI = `/${process.env.REACT_APP_RWAPI_ENDPOINT}/api/${appProvinceShort}/${firm.id}/${AppID}`;
    const STORAGE_PATH = `Analyzer`;
    const docID: string = uuidv4();
    setDocID(docID);
    const docName = `${docID}.pdf`; // set unique id to doc

    try {
      // step 1: get storage url
      const arrayBuffer = await doc.arrayBuffer();
      fetch(
        `${RWAPI}/filedrive/storageDocUpload?SessionID=${sid}&name=${encodeURIComponent(docName)}&path=${encodeURIComponent(STORAGE_PATH)}`,
        {
          headers: {
            "Content-Type": "application/octet-stream",
          },
          method: "POST",
          body: arrayBuffer,
        },
      ).then(async (step1Res) => {
          if (step1Res && step1Res.ok) {
            // step 2: call analyzer
            fetch(
              `${RWAPI}/filedrive/analyzeDoc?SessionID=${sid}&name=${encodeURIComponent(docName)}&path=${encodeURIComponent(STORAGE_PATH)}`,
              {
                method: "POST",
              },
            ).then(async (step2Res) => {
              if (step2Res && step2Res.ok) {
                const responseBody = await step2Res.json();
                const data = responseBody.result;
                data["id"] = docID;
                processAnalyzedData(data);
                successCallback(data);
              } else {
                fetch(
                  `${RWAPI}/filedrive/autogen?SessionID=${sid}&name=${encodeURIComponent(docName)}&path=${encodeURIComponent(STORAGE_PATH)}`,
                  {
                    method: "POST",
                    body: JSON.stringify({
                      fileName: doc?.name,
                      size: doc?.size,
                      lastModified: doc?.lastModified,
                      docType: "mortgage",
                      requestType: "extraction-text",
                      psm: null,
                    }),
                  },
                )
                  .then(async (analysisRes) => {
                    if (analysisRes && analysisRes.ok) {
                      const responseBody = await analysisRes.json();
                      const data = responseBody.result.result;
                      data["id"] = docID;
                      processAnalyzedData(data);
                      successCallback(data);
                    } else {
                      const errorResponse = await analysisRes.json();
                      throw new Error(
                        JSON.stringify({
                          message:
                            errorResponse.error ||
                            "Failed to get analyzed data",
                          result: errorResponse?.result,
                        }),
                      );
                    }
                  })
                  .catch((err) => {
                    try {
                      const errorData = JSON.parse(err.message);
                      failedCallback(errorData.result);
                    } catch {
                      failedCallback();
                    }
                  });
              }
            });
          } else {
            throw Error("Failed to upload doc.");
          }
        })
        .catch(() => {
          failedCallback();
          console.log("comes  =========");
        });
    } catch (e) {
      //
      console.log("comes here");
      failedCallback();
    }
  }

  export function processAnalyzedData(data) {
    const MoneyField = [];
    const DateField = [];
    [fieldDef.Lender, fieldDef.MortgageInfo, fieldDef.ChargeProv].forEach(
      (cate) => {
        cate.forEach((def) => {
          if (def.type === "date") {
            DateField.push(def.attribute);
          }
          if (def.type === "money") {
            MoneyField.push(def.attribute);
          }
        });
      },
    );
    if (!data["LenderRefNo"] && data["cmhcReference"]) {
      data["LenderRefNo"] = data["cmhcReference"];
    }
    MoneyField.forEach((field) => {
      try {
        if (data[field]) {
          data[field] = data[field].replaceAll("$", "");
          data[field] = data[field].replaceAll(" ", "");
          data[field] = data[field].replaceAll(",", "");
        }
      } catch (err) {
        console.log(
          `processAnalyzedData: failed to convert ${field} (${data[field]})`,
        );
      }
    });
    DateField.forEach((field) => {
      try {
        if (data[field]) {
          const d = new Date(data[field]);
          if (d.toString() !== "Invalid Date") {
            data[field] = FormatDateDataType(d.toISOString(), "dfca");
          }
          // console.log(data[field]);
        }
      } catch (err) {
        console.log(
          `processAnalyzedData: failed to convert ${field} (${data[field]})`,
        );
      }
    });
    // calc LastPaymentDate
    if (!data["LastPaymentDate"] && data["FirstPaymentDate"] && data["Term"]) {
      const yearReg = /(\d+)\s*year/;
      let arr = yearReg.exec(data["Term"]);
      const lastPayDate = new Date(data["FirstPaymentDate"]);
      if (arr && arr[1]) {
        lastPayDate.setFullYear(
          lastPayDate.getFullYear() + parseInt(arr[1], 10),
        );
        lastPayDate.setMonth(lastPayDate.getMonth() - 1);
        data["LastPaymentDate"] = FormatDateDataType(
          lastPayDate.toISOString(),
          "dfca",
        );
      } else {
        const monthReg = /(\d+)\s*month/;
        arr = monthReg.exec(data["Term"]);
        if (arr && arr[1]) {
          lastPayDate.setMonth(
            lastPayDate.getMonth() + parseInt(arr[1], 10) - 1,
          );
          data["LastPaymentDate"] = FormatDateDataType(
            lastPayDate.toISOString(),
            "dfca",
          );
        }
      }
    }
    // calc Term
    if (data["LastPaymentDate"] && data["FirstPaymentDate"] && !data["Term"]) {
      const firstPayDate = new Date(data["FirstPaymentDate"]);
      const lastPayDate = new Date(data["LastPaymentDate"]);
      let months;
      months = (lastPayDate.getFullYear() - firstPayDate.getFullYear()) * 12;
      months -= firstPayDate.getMonth();
      months += lastPayDate.getMonth();
      data["Term"] = `${months} month${months > 1 ? "s" : ""}`;
    }
  }

  export async function sendEmail(
    data: EmailBody,
    SESSION_INFO: lddGlobalSessionInfo,
    callback: (...arg) => void,
  ) {
    const { appProvinceShort, firm, sid } = SESSION_INFO;
    const RWAPI = `/${process.env.REACT_APP_RWAPI_ENDPOINT}/api/${appProvinceShort}/${firm.id}`;
    try {
      fetch(`${RWAPI}/sendInternalEmail?SessionID=${sid}`, {
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
        body: JSON.stringify(data),
      }).then(() => {
        if (typeof callback === "function") {
          callback();
        }
      });
    } catch (err) {
      console.log("failed to send internal email");
    }
  }

  export async function sendOutboundEmail(
    data: EmailBody,
    SESSION_INFO: lddGlobalSessionInfo,
    callback: (...arg) => void,
  ) {
    const { appProvinceShort, firm, sid } = SESSION_INFO;
    const RWAPI = `/${process.env.REACT_APP_RWAPI_ENDPOINT}/api/${appProvinceShort}/${firm.id}`;
    try {
      fetch(`${RWAPI}/sendOutboundEmail?SessionID=${sid}`, {
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
        body: JSON.stringify(data),
      }).then(() => {
        if (typeof callback === "function") {
          callback();
        }
      });
    } catch (err) {
      console.log("failed to send internal email");
    }
  }

  export async function getAnalyzerResponse(
    SESSION_INFO: lddGlobalSessionInfo,
    fileLocation: string,
    successCallback: (...arg) => void,
  ) {
    const { appProvinceShort, firm, sid } = SESSION_INFO;
    const RWAPI = `/${process.env.REACT_APP_RWAPI_ENDPOINT}/api/${appProvinceShort}/${firm.id}`;
    fileLocation = encodeURIComponent(fileLocation);
    try {
      const response = await fetch(
        `${RWAPI}/analyzerResponse?SessionID=${sid}&fileLocation=${fileLocation}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
          method: "GET",
        },
      );
      if (response.status === 200) {
        const payload = await response.json();
        const data = payload.result;
        const fileLocationElements = fileLocation.split("/");
        const docID =
          fileLocationElements[fileLocationElements.length - 1].split(".")[0];
        data["id"] = docID;
        processAnalyzedData(data);
        successCallback(data);
      }
    } catch (err) {
      console.log("Failed to Fetch Branch Names for the given Lender ID");
    }
  }

  export async function getLenderBranches(
    LDDLenderID: string,
    SESSION_INFO: lddGlobalSessionInfo,
  ) {
    const { appProvinceShort, firm, sid } = SESSION_INFO;
    const RWAPI = `/${process.env.REACT_APP_RWAPI_ENDPOINT}/api/${appProvinceShort}/${firm.id}`;
    try {
      const response = await fetch(
        `${RWAPI}/lenderBranches?SessionID=${sid}&LDDLenderID=${LDDLenderID}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
          method: "GET",
        },
      );
      if (response.status === 200) {
        const payload = await response.json();
        const result = payload.result;
        if (result.length > 0) {
          return result;
        }
        return [];
      }
    } catch (err) {
      console.log("Failed to Fetch Branch Names for the given Lender ID");
    }
  }

  export async function getAllLendersInProvince(
    SESSION_INFO: lddGlobalSessionInfo,
  ) {
    const { appProvinceShort, firm, sid } = SESSION_INFO;
    const RWAPI = `/${process.env.REACT_APP_RWAPI_ENDPOINT}/api/${appProvinceShort}/${firm.id}`;
    try {
      const response = await fetch(`${RWAPI}/searchLenders?SessionID=${sid}`, {
        headers: {
          "Content-Type": "application/json",
        },
        method: "GET",
      });
      if (response.status === 200) {
        const payload = await response.json();
        const result = payload.result;
        if (result.length > 0) {
          return result;
        }
        return [];
      }
    } catch (err) {
      console.log("Failed to Fetch Lenders Names for the given Lender ID");
    }
  }

  export async function getLenderConfigByName(
    lenderName: string,
    SESSION_INFO: lddGlobalSessionInfo,
  ) {
    const { appProvinceShort, firm, sid } = SESSION_INFO;
    const RWAPI = `/${process.env.REACT_APP_RWAPI_ENDPOINT}/api/${appProvinceShort}/${firm.id}`;
    try {
      const response = await fetch(
        `${RWAPI}/lenderConfigByName?SessionID=${sid}&lenderName=${lenderName}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      if (response.status === 200) {
        const payload = await response.json();
        const result = payload.result;
        if (result && Object.keys(result).length > 0) {
          return result;
        }
        return {};
      }
    } catch (err) {
      console.log("Failed to Fetch Lender Config by Name");
    }
  }

  export async function getBranchDetails(
    LenderContactID: number,
    SESSION_INFO: lddGlobalSessionInfo,
  ) {
    const { appProvinceShort, firm, sid } = SESSION_INFO;
    const RWAPI = `/${process.env.REACT_APP_RWAPI_ENDPOINT}/api/${appProvinceShort}/${firm.id}`;
    try {
      const response = await fetch(
        `${RWAPI}/branchDetails?SessionID=${sid}&LenderContactID=${LenderContactID}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
          method: "GET",
        },
      );
      if (response.status === 200) {
        const payload = await response.json();
        const result = payload.result;
        if (result.length > 0) {
          return result[0];
        }
        return [];
      }
    } catch (err) {
      console.log(
        "Failed to Fetch Branch Details for the given LenderContactID",
      );
    }
  }
}

export default AnalyzerFuncs;
